import type {
  PostMessageResult,
  SessionBootstrap,
  WidgetMessage,
  WidgetSettings,
} from "@/types";

/**
 * Tiny fetch wrapper for the widget's calls to /api/webchat/*.
 *
 * No axios — bundle size matters. Handles JSON + multipart, tacks on the
 * visitor session token when present, and surfaces structured errors.
 */
export class WebchatApi {
  private sessionToken: string | null = null;

  constructor(private readonly settings: WidgetSettings) {}

  setSessionToken(token: string) {
    this.sessionToken = token;
  }

  async createSession(visitorId: string | undefined): Promise<SessionBootstrap> {
    return this.request<SessionBootstrap>("POST", "/session", {
      body: {
        siteKey: this.settings.siteKey,
        visitorId,
        identity: this.settings.identity,
        identityHash: this.settings.identityHash,
        currentUrl:
          typeof window !== "undefined" ? window.location.href : undefined,
        referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      },
    });
  }

  async listMessages(): Promise<{ data: WidgetMessage[] }> {
    return this.request<{ data: WidgetMessage[] }>("GET", "/messages");
  }

  async postMessage(payload: {
    content?: string;
    type?: string;
    mediaUrl?: string;
    mimeType?: string;
    fileName?: string;
    replyToId?: string;
  }): Promise<PostMessageResult> {
    const res = await this.request<PostMessageResult>("POST", "/messages", {
      body: payload,
    });
    // The very first message upgrades the session — server attaches a fresh
    // token bound to the newly-created Conversation. Mirror it locally so
    // subsequent calls (typing, uploads, reads) carry the upgraded JWT.
    if (res?.sessionToken) {
      this.sessionToken = res.sessionToken;
    }
    return res;
  }

  async typing(isTyping: boolean): Promise<void> {
    await this.request("POST", "/typing", { body: { isTyping } });
  }

  async markRead(messageIds?: string[]): Promise<void> {
    await this.request("POST", "/read", { body: { messageIds } });
  }

  async uploadFile(file: File): Promise<{
    mediaUrl: string;
    mimeType: string;
    fileName: string;
    size: number;
  }> {
    const form = new FormData();
    form.append("file", file);
    return this.request("POST", "/uploads", {
      body: form,
      isMultipart: true,
    });
  }

  async close(): Promise<void> {
    await this.request("POST", "/close");
  }

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      isMultipart?: boolean;
    } = {},
  ): Promise<T> {
    const url = `${this.settings.apiUrl}/api/webchat${path}`;
    const headers: Record<string, string> = {};
    if (this.sessionToken) {
      headers.Authorization = `Bearer ${this.sessionToken}`;
    }

    let body: BodyInit | undefined;
    if (options.body !== undefined) {
      if (options.isMultipart) {
        body = options.body as FormData;
      } else {
        body = JSON.stringify(options.body);
        headers["Content-Type"] = "application/json";
      }
    }

    const res = await fetch(url, {
      method,
      headers,
      body,
      credentials: "omit",
    });

    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const data = (await res.json()) as { message?: string };
        if (data?.message) message = data.message;
      } catch {
        // ignore parse error
      }
      throw new WebchatApiError(res.status, message);
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }
}

export class WebchatApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "WebchatApiError";
  }
}
